import { Injectable } from '@angular/core';
import { TenantSettings } from './tenant-settings.service';

/**
 * Service responsible for loading and managing the Joule Web Client integration.
 * Encapsulates all Joule-related logic: script loading, webclient polling,
 * Bridge API registration, and active state tracking.
 */
@Injectable({ providedIn: 'root' })
export class JouleService {
  private jouleLoaded = false;
  private jouleLoading = false;
  private jouleBridgeRegistered = false;

  /** Whether the Joule chat panel is currently open */
  public jouleActive = false;

  constructor(private tenantSettings: TenantSettings) {}

  /** Whether Joule integration is enabled and configured for this tenant */
  public isEnabled(): boolean {
    return !!this.tenantSettings.isJouleIntegrationEnabled && !!this.tenantSettings.jouleTenantUrl;
  }

  /**
   * Toggles the Joule chat panel. On first call, dynamically loads the bootstrap
   * script and polls for the webclient to become available.
   */
  public toggle(): void {
    if (!this.jouleLoaded && !this.jouleLoading) {
      this.loadWebClient();

      const checkInterval = setInterval(() => {
        if ((window as any).sap?.das?.webclient) {
          clearInterval(checkInterval);
          this.registerBridgeCallbacks();
          (window as any).sap.das.webclient.toggle();
          this.jouleActive = !this.jouleActive;
        }
      }, 100);
      setTimeout(() => clearInterval(checkInterval), 10000);
    } else if (this.jouleLoading) {
      return;
    } else if ((window as any).sap?.das?.webclient) {
      this.registerBridgeCallbacks();
      (window as any).sap.das.webclient.toggle();
      this.jouleActive = !this.jouleActive;
    }
  }

  /**
   * Dynamically injects the Joule bootstrap script into the page.
   * Uses createElement('script') for lazy/conditional loading so the external
   * Joule Web Client is only fetched when the integration is enabled and the user
   * interacts with the Joule icon (or on pre-load).
   */
  private loadWebClient(): void {
    if (this.jouleLoaded || this.jouleLoading || !this.isEnabled()) return;

    this.jouleLoading = true;
    const script = document.createElement('script');
    script.src = this.tenantSettings.jouleTenantUrl + '/resources/public/webclient/bootstrap.js';
    script.setAttribute('data-bot-name', this.tenantSettings.jouleDigitalAssistantName || '');
    script.setAttribute('data-expander-type', 'CUSTOM');
    script.async = true;
    script.onload = () => {
      this.jouleLoaded = true;
      this.jouleLoading = false;
    };
    script.onerror = (e) => {
      this.jouleLoading = false;
      console.error('Failed to load Joule Web Client bootstrap script', e);
    };
    document.head.appendChild(script);
  }

  /**
   * Registers callbacks with the Joule Web Client Bridge API to track open/close state.
   * This ensures the header icon stays in sync even when the user closes the Joule
   * chat panel via the X button inside the panel (not just via the header icon click).
   */
  private registerBridgeCallbacks(): void {
    if (this.jouleBridgeRegistered) return;
    const webclient = (window as any).sap?.das?.webclient;
    if (webclient && webclient.registerBridgeCallbacks) {
      webclient.registerBridgeCallbacks('cpqSetup', {
        onOpen: () => { this.jouleActive = true; },
        onClose: () => { this.jouleActive = false; }
      }, this);
      this.jouleBridgeRegistered = true;
    }
  }
}